import { createBrowserRouter, Navigate, type RouteObject } from 'react-router-dom'
import { AppShell } from '@/shell/AppShell'
import { AuthLayout } from '@/shell/AuthLayout'
import { PlatformAdminShell } from '@/shell/PlatformAdminShell'
import { requireAuth, requirePermission, requireSmbScoped, requirePlatformAccess } from '@/auth/requirePermission'
import { SecurityPage } from '@/pages/security/SecurityPage'
import { ProfilePage } from '@/pages/profile/ProfilePage'
import { CompanyPage } from '@/pages/company/CompanyPage'
import { BrandingPage } from '@/pages/branding/BrandingPage'
import { WorkflowsPage } from '@/pages/workflows/WorkflowsPage'
import { AgentsPage } from '@/pages/agents/AgentsPage'
import { TeamManagementPage } from '@/pages/team/TeamManagementPage'
import { CoaPage } from '@/pages/coa/CoaPage'
import { JournalEntryPage } from '@/pages/journal-entry/JournalEntryPage'
import { ViewStatementsPage } from '@/pages/view-statements/ViewStatementsPage'
import { TbImportPage } from '@/pages/tb-import/TbImportPage'
import { AccrualSchedulePage } from '@/pages/accrual-schedule/AccrualSchedulePage'
import { FixedAssetsPage } from '@/pages/fixed-assets/FixedAssetsPage'
import { PrepaidPage } from '@/pages/prepaid/PrepaidPage'
import { LoanAmortizationPage } from '@/pages/loan-amortization/LoanAmortizationPage'
import { DataHistoryPage } from '@/pages/data-history/DataHistoryPage'
import { YearEndClosePage } from '@/pages/year-end-close/YearEndClosePage'
import { OverviewPage } from '@/pages/overview/OverviewPage'
import { AnalyzeStatementsPage } from '@/pages/analyze-statements/AnalyzeStatementsPage'
import { BudgetPlanningPage } from '@/pages/budget-planning/BudgetPlanningPage'
import { ReportingConfigPage } from '@/pages/reporting-config/ReportingConfigPage'
import { ReportingPage } from '@/pages/reporting/ReportingPage'
import { ReconcilePage } from '@/pages/reconcile/ReconcilePage'
import { UploadPage } from '@/pages/upload/UploadPage'
import { ContactSalesPage } from '@/pages/contact-sales/ContactSalesPage'
import { MyQueriesPage } from '@/pages/contact-sales/MyQueriesPage'
import { QueryDetailPage } from '@/pages/contact-sales/QueryDetailPage'
import { GuidePage } from '@/pages/guide/GuidePage'
import { SubscriptionsPage } from '@/pages/subscriptions/SubscriptionsPage'
import { ChatbotPage } from '@/pages/chatbot/ChatbotPage'
import { OverviewPage as PlatformOverviewPage } from '@/pages/platform-admin/OverviewPage'
import { TenantsPage as PlatformTenantsPage } from '@/pages/platform-admin/TenantsPage'
import { CustomerUsersPage as PlatformCustomerUsersPage } from '@/pages/platform-admin/CustomerUsersPage'
import { StaffPage as PlatformStaffPage } from '@/pages/platform-admin/StaffPage'
import { StaffInvitationsPage as PlatformStaffInvitationsPage } from '@/pages/platform-admin/StaffInvitationsPage'
import { StaffRolesPage as PlatformStaffRolesPage } from '@/pages/platform-admin/StaffRolesPage'
import { AuditPage as PlatformAuditPage } from '@/pages/platform-admin/AuditPage'
import { DbToolsPage as PlatformDbToolsPage } from '@/pages/platform-admin/DbToolsPage'
import { PlaidPage } from '@/pages/plaid/PlaidPage'
import { InboxPage } from '@/pages/inbox/InboxPage'
import { InboxSettingsPage } from '@/pages/inbox/InboxSettingsPage'
import { LoginPage } from '@/pages/auth/LoginPage'
import { SignupPage } from '@/pages/auth/SignupPage'
import { ForgotPasswordPage } from '@/pages/auth/ForgotPasswordPage'
import { WorkspacePickerPage } from '@/pages/auth/WorkspacePickerPage'
import { ResetPasswordPage } from '@/pages/auth/ResetPasswordPage'
import { VerifyEmailPage } from '@/pages/auth/VerifyEmailPage'
import { AcceptInvitePage } from '@/pages/auth/AcceptInvitePage'
import { SessionExpiredPage } from '@/pages/auth/SessionExpiredPage'
import { LogoutPage } from '@/pages/auth/LogoutPage'
import { AdminLoginPage } from '@/pages/auth/AdminLoginPage'
import { NotFoundPage } from '@/pages/errors/NotFoundPage'
import { ForbiddenPage } from '@/pages/errors/ForbiddenPage'
import { RouteErrorPage } from '@/pages/errors/RouteErrorPage'
import { PlaidOAuthCallbackPage } from '@/pages/plaid-oauth-callback/PlaidOAuthCallbackPage'
import { SharedChatPage } from '@/features/chatbot/SharedChatPage'
import { InvoicesListPage } from '@/pages/invoicing/invoices/InvoicesListPage'
import { InvoiceEditorPage } from '@/pages/invoicing/invoices/InvoiceEditorPage'
import { InvoiceDetailPage } from '@/pages/invoicing/invoices/InvoiceDetailPage'
import { CustomersListPage } from '@/pages/invoicing/customers/CustomersListPage'
import { CustomerEditorPage } from '@/pages/invoicing/customers/CustomerEditorPage'
import { HostedInvoicePaymentPage } from '@/pages/pay/HostedInvoicePaymentPage'
import { CustomerPortalPage } from '@/pages/portal/CustomerPortalPage'
import { ArAgingPage } from '@/pages/invoicing/reports/ArAgingPage'
import { ApAgingPage } from '@/pages/invoicing/reports/ApAgingPage'
import { RecurringInvoicesPage } from '@/pages/invoicing/recurring/RecurringInvoicesPage'
import { ApprovalInboxPage } from '@/pages/approvals/ApprovalInboxPage'
import { ApprovalPoliciesPage } from '@/pages/approvals/ApprovalPoliciesPage'
import { ApprovalInstanceDetailPage } from '@/pages/approvals/ApprovalInstanceDetailPage'
import { ApprovalDelegationsPage } from '@/pages/approvals/ApprovalDelegationsPage'
import { PayrollDashboardPage } from '@/pages/payroll/PayrollDashboardPage'
import { EmployeesListPage } from '@/pages/payroll/EmployeesListPage'
import { EmployeeEditorPage } from '@/pages/payroll/EmployeeEditorPage'
import { ContractorsListPage } from '@/pages/payroll/ContractorsListPage'
import { PayComponentsListPage } from '@/pages/payroll/PayComponentsListPage'
import { PayRunsListPage } from '@/pages/payroll/PayRunsListPage'
import { PayRunDetailPage } from '@/pages/payroll/PayRunDetailPage'
import { PaySchedulesSettingsPage } from '@/pages/payroll/PaySchedulesSettingsPage'
import { TaxEstimatePage } from '@/pages/tax/TaxEstimatePage'
import { FilingDetailPage } from '@/pages/tax/FilingDetailPage'
import { PeopleHub, TimeLeaveHub, CompensationHub, BenefitsHub, PaymentsHub, PayrollSettingsHub } from '@/pages/payroll/Hubs'
import { FilingsHub, SalesTaxHub, ComplianceHub, TaxSetupHub } from '@/pages/tax/Hubs'
import {
  MyAccountIndexPage,
  MyPayslipsPage,
  MyTaxFormsPage,
  MyW4Page,
  MyDirectDepositPage,
} from '@/pages/account/EssPages'
import {
  HealthcareCredentialsPage,
  PrevailingWagePage,
  DotDriverHoursPage,
  TipPoolsPage,
  AcademicContractsPage,
  UnionsPage,
  MigrationImporterPage,
  FirmPracticePage,
  WebhooksPage,
} from '@/pages/payroll/IndustryAndMigrationPages'
import {
  OnboardingPage,
  TimesheetsPage,
  LeavePage,
  HolidaysPage,
  SchedulesPage,
  SalaryStructuresPage,
  PayGradesPage,
  EquityPage,
  ReviewCyclesPage,
  BenefitPlansPage,
  BenefitEnrollmentPage,
  RetirementPage,
  HsaFsaPage,
  AcaPage,
  NachaPage,
  ChecksPage,
  PaymentBatchesPage,
  PayrollReportsPage,
} from '@/pages/payroll/WorkforceExtraPages'
import { BenefitsMarketplacePage } from '@/pages/payroll/BenefitsMarketplacePage'
import {
  TaxDashboardPage,
  TaxCalendarPage,
  AllFilingsPage,
  PayrollFilingsPage,
  ContractorFilingsPage,
  SalesFilingsPage,
  BusinessFilingsPage,
  PersonalFilingsPage,
  TaxPaymentsPage,
  JurisdictionsPage,
  NexusTrackerPage,
  RateLookupPage,
  ExemptionCertsPage,
  TaxDocumentsPage,
  ReconciliationPage as TaxReconciliationPage,
  TaxReportsPage,
  TaxSettingsPage,
} from '@/pages/tax/TaxPages'
import { VendorsListPage } from '@/pages/invoicing/vendors/VendorsListPage'
import { VendorDetailPage } from '@/pages/invoicing/vendors/VendorDetailPage'
import { BillsListPage } from '@/pages/invoicing/bills/BillsListPage'
import { BillEditorPage } from '@/pages/invoicing/bills/BillEditorPage'
import { BillDetailPage } from '@/pages/invoicing/bills/BillDetailPage'
import { PaymentsDashboardPage } from '@/pages/invoicing/payments/PaymentsDashboardPage'
import { ManagedPayoutsPage as PlatformManagedPayoutsPage } from '@/pages/platform-admin/ManagedPayoutsPage'
import { ContactMessagesPage as PlatformContactMessagesPage } from '@/pages/platform-admin/ContactMessagesPage'
import { ContactMessageDetailPage as PlatformContactMessageDetailPage } from '@/pages/platform-admin/ContactMessageDetailPage'
import { AnnouncementsPage as PlatformAnnouncementsPage } from '@/pages/platform-admin/AnnouncementsPage'

/**
 * React Router v6 route table. The whole tree is mounted under /app via
 * <BrowserRouter basename="/app">. As pages get ported, additional routes
 * are inserted under the AppShell layout (and /login etc. under AuthLayout).
 */

const routes: RouteObject[] = [
  {
    path: '/',
    element: <AppShell />,
    errorElement: <RouteErrorPage />,
    loader: requireAuth(),
    children: [
      { index: true, element: <Navigate to="/dashboard" replace /> },

      // Dashboard - the Overview page (KPI cards + charts + recent txns +
      // budget widgets). Replaces the old welcome/quick-link landing.
      {
        path: 'dashboard',
        element: <OverviewPage />,
        loader: requirePermission('dashboard.view'),
      },

      // Phase 1a - Security (2FA enrol/disable).
      {
        path: 'security',
        element: <SecurityPage />,
        loader: requirePermission('security.manage'),
      },

      // Phase 2a - Profile (name, avatar, delete-account with email code).
      {
        path: 'profile',
        element: <ProfilePage />,
        loader: requirePermission('profile.view'),
      },

      // NOTE: there is a parallel `/workspaces` route registered under
      // the AuthLayout block below. That one handles the post-OAuth /
      // post-login picker (no App JWT yet). DO NOT register `/workspaces`
      // here under AppShell - see the deleted-entry post-mortem in
      // commit 2aceb5d. We register an explicitly different in-app path
      // (`/account/workspaces`) so the in-app "Switch workspace" link
      // renders inside the sidebar/header chrome instead of bouncing to
      // the bare AuthLayout. WorkspacePickerPage auto-detects the mode
      // (pending vs authed) so the same component works in both.
      {
        path: 'account/workspaces',
        element: <WorkspacePickerPage variant="embedded" />,
        loader: requirePermission('profile.view'),
      },

      // Phase 2b - Company (tenant business + regional + session settings).
      {
        path: 'company',
        element: <CompanyPage />,
        loader: requirePermission('tenant.settings.view'),
      },

      // Branding - per-tenant logo + brand colour. Lives next to Company
      // Settings under Account in the sidebar; both gate on the same
      // tenant.settings.* permissions.
      {
        path: 'branding',
        element: <BrandingPage />,
        loader: requirePermission('tenant.settings.view'),
      },

      // Phase 2d - Workflows (list + actions + runs history).
      {
        path: 'workflows',
        element: <WorkflowsPage />,
        loader: requirePermission('workflows.view'),
      },

      // Phase 2e - Agents (list + delete; chat + create/edit link out to legacy).
      {
        path: 'agents',
        element: <AgentsPage />,
        loader: requirePermission('agents.view'),
      },

      // Phase 2f - Team management (members tab; invitations/roles/api-keys/audit deferred).
      {
        path: 'team-management',
        element: <TeamManagementPage />,
        loader: requirePermission('team.view'),
      },

      // Phase 2g - Chart of accounts (BS/IS toggle, category+subcategory CRUD, CSV/PDF export).
      {
        path: 'coa',
        element: <CoaPage />,
        loader: requirePermission('accounting.coa'),
      },


      // Phase 2h - Journal entries (list + form, Post/Save/Void, debit-credit balance guard).
      {
        path: 'journal-entry',
        element: <JournalEntryPage />,
        loader: requirePermission('accounting.journal_entries'),
      },

      // Phase 2i - View statements (paginated list, filters, stats, bulk delete, CSV/PDF export).
      {
        path: 'view-statements',
        element: <ViewStatementsPage />,
        loader: requirePermission('statements.view'),
      },

      // Phase 3a - Trial balance import (5-step wizard: upload → map → validate → import → verify).
      {
        path: 'tb-import',
        element: <TbImportPage />,
        loader: requirePermission('accounting.tb_import'),
      },

      // Phase 3b - Accrual schedule (inline-edit table, bulk save, post to GL).
      {
        path: 'accrual-schedule',
        element: <AccrualSchedulePage />,
        loader: requirePermission('accounting.accruals'),
      },

      // Phase 3c - Fixed assets (depreciation schedule form, preview, save, post, view, void).
      {
        path: 'fixed-assets',
        element: <FixedAssetsPage />,
        loader: requirePermission('accounting.allocations'),
      },

      // Phase 3d - Prepaid expenses (straight-line amortization schedule).
      {
        path: 'prepaid',
        element: <PrepaidPage />,
        loader: requirePermission('accounting.allocations'),
      },

      // Phase 3e - Loan amortization (standard formula, principal/interest split, post to GL).
      {
        path: 'loan-amortization',
        element: <LoanAmortizationPage />,
        loader: requirePermission('accounting.allocations'),
      },

      // Phase 3f - Data ingestion history (Plaid/PDF/CSV tabs, per-row delete + delete-all).
      {
        path: 'data-history',
        element: <DataHistoryPage />,
        loader: requirePermission('statements.history'),
      },

      // Phase 3g - Year-end close (fiscal year cards, preview + confirm close, reverse close).
      {
        path: 'year-end-close',
        element: <YearEndClosePage />,
        loader: requirePermission('accounting.year_end_close'),
      },

      // Legacy /overview URL - Dashboard is now the Overview. Redirect so
      // any old inbound links (guide, bookmarks) still resolve.
      { path: 'overview', element: <Navigate to="/dashboard" replace /> },

      // Phase 3i - Analyze statements (category spending bar/scatter/pie).
      {
        path: 'analyze-statements',
        element: <AnalyzeStatementsPage />,
        loader: requirePermission('statements.analyze'),
      },

      // Phase 3j - Budget planning (allocations, sliders, AI suggestions, recurring).
      {
        path: 'budget-planning',
        element: <BudgetPlanningPage />,
        loader: requirePermission('accounting.budget'),
      },

      // Phase 3k - Reporting config (CF mappings, periods, account mappings, snapshots).
      {
        path: 'reporting-config',
        element: <ReportingConfigPage />,
        loader: requirePermission('accounting.reporting_config'),
      },

      // Phase 3l - Reporting (BS / IS / CF / TB with drill-down + snapshot + PDF/CSV).
      {
        path: 'reporting',
        element: <ReportingPage />,
        loader: requirePermission('accounting.reporting'),
      },

      // Phase 2k - Reconcile (compute system vs bank balance, save reconciliation).
      {
        path: 'reconcile',
        element: <ReconcilePage />,
        loader: requirePermission('accounting.reconcile'),
      },

      // Phase 2l - Upload wizard (form → tables → duplicates → classify → confirm → results).
      {
        path: 'upload',
        element: <UploadPage />,
        loader: requirePermission('statements.upload'),
      },

      // Contact us - picker + form hub, plus the customer-facing thread
      // surfaces (your queries list and per-query detail with reply box).
      {
        path: 'contact-sales',
        element: <ContactSalesPage />,
        loader: requirePermission('support.contact_sales'),
      },
      {
        path: 'contact-sales/queries',
        element: <MyQueriesPage />,
        loader: requirePermission('support.contact_sales'),
      },
      {
        path: 'contact-sales/queries/:id',
        element: <QueryDetailPage />,
        loader: requirePermission('support.contact_sales'),
      },

      // Phase 5b - User guide (static documentation, no permission gate).
      { path: 'guide', element: <GuidePage /> },

      // Phase 5c - Subscriptions (pricing grid, current-plan highlight, Stripe checkout).
      {
        path: 'subscriptions',
        element: <SubscriptionsPage />,
        loader: requirePermission('billing.view'),
      },

      // Phase 5d - Smart Chat (chat UI, agents + workflows popover, markdown).
      {
        path: 'chatbot',
        element: <ChatbotPage />,
        loader: requirePermission('ai.chatbot'),
      },

      // Phase 5g - Plaid bank feeds (Plaid Link SDK + classification flow).
      {
        path: 'plaid',
        element: <PlaidPage />,
        loader: requirePermission('banking.plaid'),
      },

      // Inbox (Phase 1) - Gmail OAuth + email fetch into Bokapsys.
      {
        path: 'inbox',
        element: <InboxPage />,
        loader: requirePermission('inbox.view'),
      },
      {
        path: 'inbox/settings',
        element: <InboxSettingsPage />,
        loader: requirePermission('inbox.view'),
      },

      // Invoicing Phase 1 - AR. See plan §1.4. Phase 4 adds /invoicing/bills.
      {
        path: 'invoicing/invoices',
        element: <InvoicesListPage />,
        loader: requirePermission('invoicing.invoices.view'),
      },
      {
        path: 'invoicing/invoices/new',
        element: <InvoiceEditorPage />,
        loader: requirePermission('invoicing.invoices.create'),
      },
      {
        path: 'invoicing/invoices/:id',
        element: <InvoiceDetailPage />,
        loader: requirePermission('invoicing.invoices.view'),
      },
      {
        path: 'invoicing/invoices/:id/edit',
        element: <InvoiceEditorPage />,
        loader: requirePermission('invoicing.invoices.create'),
      },
      {
        path: 'invoicing/customers',
        element: <CustomersListPage />,
        loader: requirePermission('invoicing.customers.view'),
      },
      {
        path: 'invoicing/customers/new',
        element: <CustomerEditorPage />,
        loader: requirePermission('invoicing.customers.manage'),
      },
      {
        path: 'invoicing/customers/:id/edit',
        element: <CustomerEditorPage />,
        loader: requirePermission('invoicing.customers.manage'),
      },
      // Phase 2b - Recurring invoice templates
      {
        path: 'invoicing/recurring',
        element: <RecurringInvoicesPage />,
        loader: requirePermission('invoicing.recurring.manage'),
      },
      // Feature 2.2 - unified payments dashboard (Connect + managed).
      {
        path: 'invoicing/payments',
        element: <PaymentsDashboardPage />,
        loader: requirePermission('invoicing.payouts.view'),
      },
      // Phase 2d - AR Aging report sits under /invoicing/reports/ar-aging so
      // the existing sidebar structure can group all invoicing surfaces.
      {
        path: 'invoicing/reports/ar-aging',
        element: <ArAgingPage />,
        loader: requirePermission('invoicing.ar.aging.view'),
      },
      // Phase 4 - AP (vendors + bills + AP aging)
      {
        path: 'invoicing/vendors',
        element: <VendorsListPage />,
        loader: requirePermission('invoicing.vendors.view'),
      },
      {
        path: 'invoicing/vendors/:id',
        element: <VendorDetailPage />,
        loader: requirePermission('invoicing.vendors.view'),
      },
      {
        path: 'invoicing/reports/ap-aging',
        element: <ApAgingPage />,
        loader: requirePermission('invoicing.ap.aging.view'),
      },
      {
        path: 'invoicing/bills',
        element: <BillsListPage />,
        loader: requirePermission('invoicing.bills.view'),
      },
      {
        path: 'invoicing/bills/new',
        element: <BillEditorPage />,
        loader: requirePermission('invoicing.bills.create'),
      },
      {
        path: 'invoicing/bills/:id',
        element: <BillDetailPage />,
        loader: requirePermission('invoicing.bills.view'),
      },

      // Approval workflow engine (Phase 3a). Inbox + policies surface;
      // Phase 3b hooks AR mutations through the engine.
      {
        path: 'approvals',
        element: <ApprovalInboxPage />,
        loader: requirePermission('approvals.inbox.view'),
      },
      {
        path: 'approvals/instances/:id',
        element: <ApprovalInstanceDetailPage />,
        loader: requirePermission('approvals.inbox.view'),
      },
      {
        path: 'approvals/policies',
        element: <ApprovalPoliciesPage />,
        loader: requirePermission('approvals.policies.view'),
      },
      {
        path: 'approvals/delegations',
        element: <ApprovalDelegationsPage />,
        loader: requirePermission('approvals.delegate'),
      },

      // Payroll (Workforce OS) - skeleton routes; full pages land per the plan
      { path: 'payroll',                       element: <PayrollDashboardPage />,    loader: requirePermission('payroll.dashboard.view') },
      { path: 'payroll/runs',                  element: <PayRunsListPage />,         loader: requirePermission('payroll.runs.view') },
      { path: 'payroll/runs/:id',              element: <PayRunDetailPage />,        loader: requirePermission('payroll.runs.view') },
      { path: 'payroll/people', element: <PeopleHub />, loader: requirePermission('payroll.employees.view') },
      { path: 'payroll/time-leave', element: <TimeLeaveHub />, loader: requirePermission('payroll.time.view') },
      { path: 'payroll/compensation', element: <CompensationHub />, loader: requirePermission('payroll.compensation.view') },
      { path: 'payroll/benefits', element: <BenefitsHub />, loader: requirePermission('payroll.benefits.view') },
      { path: 'payroll/payments', element: <PaymentsHub />, loader: requirePermission('payroll.payments.view') },
      { path: 'payroll/setup', element: <PayrollSettingsHub />, loader: requirePermission('payroll.settings.view') },
      { path: 'payroll/employees',             element: <EmployeesListPage />,       loader: requirePermission('payroll.employees.view') },
      { path: 'payroll/employees/new',         element: <EmployeeEditorPage />,      loader: requirePermission('payroll.employees.manage') },
      { path: 'payroll/employees/:id',         element: <EmployeeEditorPage />,      loader: requirePermission('payroll.employees.view') },
      { path: 'payroll/contractors',           element: <ContractorsListPage />,     loader: requirePermission('payroll.contractors.view') },
      { path: 'payroll/onboarding',            element: <OnboardingPage />,          loader: requirePermission('payroll.employees.manage') },
      { path: 'payroll/time',                  element: <TimesheetsPage />,          loader: requirePermission('payroll.time.view') },
      { path: 'payroll/leave',                 element: <LeavePage />,               loader: requirePermission('payroll.leave.view') },
      { path: 'payroll/holidays',              element: <HolidaysPage />,            loader: requirePermission('payroll.holidays.manage') },
      { path: 'payroll/schedules',             element: <SchedulesPage />,           loader: requirePermission('payroll.schedules.manage') },
      { path: 'payroll/comp/structures',       element: <SalaryStructuresPage />,    loader: requireSmbScoped('payroll.compensation.view') },
      { path: 'payroll/comp/components',       element: <PayComponentsListPage />,   loader: requirePermission('payroll.components.view') },
      { path: 'payroll/comp/grades',           element: <PayGradesPage />,           loader: requireSmbScoped('payroll.compensation.view') },
      { path: 'payroll/comp/equity',           element: <EquityPage />,              loader: requireSmbScoped('payroll.equity.view') },
      { path: 'payroll/comp/cycles',           element: <ReviewCyclesPage />,        loader: requirePermission('payroll.cycles.manage') },
      { path: 'payroll/benefits/plans',        element: <BenefitPlansPage />,        loader: requirePermission('payroll.benefits.view') },
      { path: 'payroll/benefits/enrollment',   element: <BenefitEnrollmentPage />,   loader: requirePermission('payroll.benefits.enroll') },
      { path: 'payroll/benefits/retirement',   element: <RetirementPage />,          loader: requirePermission('payroll.retirement.manage') },
      { path: 'payroll/benefits/hsa-fsa',      element: <HsaFsaPage />,              loader: requirePermission('payroll.hsa_fsa.manage') },
      { path: 'payroll/benefits/aca',          element: <AcaPage />,                 loader: requirePermission('payroll.aca.view') },
      { path: 'payroll/benefits/shop',         element: <BenefitsMarketplacePage />, loader: requirePermission('payroll.benefits.view') },
      { path: 'payroll/payments/nacha',        element: <NachaPage />,               loader: requirePermission('payroll.payments.view') },
      { path: 'payroll/payments/checks',       element: <ChecksPage />,              loader: requirePermission('payroll.payments.checks') },
      { path: 'payroll/payments/batches',      element: <PaymentBatchesPage />,      loader: requirePermission('payroll.payments.batch') },
      { path: 'payroll/reports',               element: <PayrollReportsPage />,      loader: requirePermission('payroll.reports.view') },
      { path: 'payroll/settings',              element: <PaySchedulesSettingsPage />, loader: requirePermission('payroll.settings.view') },
      { path: 'payroll/industry/healthcare',   element: <HealthcareCredentialsPage />, loader: requireSmbScoped('payroll.industry.healthcare.manage') },
      { path: 'payroll/industry/construction', element: <PrevailingWagePage />,      loader: requireSmbScoped('payroll.industry.construction.manage') },
      { path: 'payroll/industry/transportation', element: <DotDriverHoursPage />,    loader: requireSmbScoped('payroll.industry.transportation.manage') },
      { path: 'payroll/industry/hospitality',  element: <TipPoolsPage />,            loader: requireSmbScoped('payroll.industry.hospitality.manage') },
      { path: 'payroll/industry/education',    element: <AcademicContractsPage />,   loader: requireSmbScoped('payroll.industry.education.manage') },
      { path: 'payroll/industry/union',        element: <UnionsPage />,              loader: requireSmbScoped('payroll.industry.union.manage') },
      { path: 'payroll/migration',             element: <MigrationImporterPage />,   loader: requireSmbScoped('payroll.migration.run') },

      { path: 'account/my-payslips',           element: <MyPayslipsPage />,          loader: requirePermission('me.payslips.view') },
      { path: 'account/my-tax-forms',          element: <MyTaxFormsPage />,          loader: requirePermission('me.tax_forms.view') },
      { path: 'account/my-w4',                 element: <MyW4Page />,                loader: requirePermission('me.w4.manage') },
      { path: 'account/my-direct-deposit',     element: <MyDirectDepositPage />,     loader: requirePermission('me.direct_deposit.manage') },
      { path: 'account/me',                    element: <MyAccountIndexPage />,      loader: requirePermission('profile.view') },
      { path: 'account/webhooks',              element: <WebhooksPage />,            loader: requireSmbScoped('webhooks.view') },
      { path: 'account/firm',                  element: <FirmPracticePage />,        loader: requireSmbScoped('firm.clients.view') },

      // Tax (Tax OS) - skeleton routes
      { path: 'tax',                           element: <TaxDashboardPage />,        loader: requirePermission('tax.dashboard.view') },
      { path: 'tax/filings-hub', element: <FilingsHub />, loader: requirePermission('tax.filings.view') },
      { path: 'tax/sales', element: <SalesTaxHub />, loader: requirePermission('tax.sales.view') },
      { path: 'tax/compliance', element: <ComplianceHub />, loader: requirePermission('tax.reconciliation.view') },
      { path: 'tax/setup', element: <TaxSetupHub />, loader: requirePermission('tax.settings.view') },
      { path: 'tax/calendar',                  element: <TaxCalendarPage />,         loader: requirePermission('tax.calendar.view') },
      { path: 'tax/filings',                   element: <AllFilingsPage />,          loader: requirePermission('tax.filings.view') },
      { path: 'tax/filings/detail/:id',        element: <FilingDetailPage />,        loader: requirePermission('tax.filings.view') },
      { path: 'tax/filings/payroll',           element: <PayrollFilingsPage />,      loader: requirePermission('tax.filings.view') },
      { path: 'tax/filings/1099',              element: <ContractorFilingsPage />,   loader: requirePermission('tax.filings.view') },
      { path: 'tax/filings/sales',             element: <SalesFilingsPage />,        loader: requirePermission('tax.sales.view') },
      { path: 'tax/filings/business',          element: <BusinessFilingsPage />,     loader: requirePermission('tax.business.view') },
      { path: 'tax/filings/personal',          element: <PersonalFilingsPage />,     loader: requireSmbScoped('tax.personal.view') },
      { path: 'tax/payments',                  element: <TaxPaymentsPage />,         loader: requirePermission('tax.payments.view') },
      { path: 'tax/jurisdictions',             element: <JurisdictionsPage />,       loader: requirePermission('tax.jurisdictions.view') },
      { path: 'tax/sales/nexus',               element: <NexusTrackerPage />,        loader: requirePermission('tax.sales.nexus.view') },
      { path: 'tax/sales/rates',               element: <RateLookupPage />,          loader: requirePermission('tax.sales.view') },
      { path: 'tax/sales/exemptions',          element: <ExemptionCertsPage />,      loader: requirePermission('tax.sales.exemptions.manage') },
      { path: 'tax/estimate',                  element: <TaxEstimatePage />,         loader: requirePermission('tax.estimate.view') },
      { path: 'tax/assistant',                 element: <TaxEstimatePage />,         loader: requirePermission('tax.estimate.view') },
      { path: 'tax/documents',                 element: <TaxDocumentsPage />,        loader: requirePermission('tax.documents.view') },
      { path: 'tax/reconciliation',            element: <TaxReconciliationPage />,   loader: requirePermission('tax.reconciliation.view') },
      { path: 'tax/reports',                   element: <TaxReportsPage />,          loader: requirePermission('tax.reports.view') },
      { path: 'tax/settings',                  element: <TaxSettingsPage />,         loader: requirePermission('tax.settings.view') },

      // 403 - surfaced by requirePermission() loader redirects.
      { path: '403', element: <ForbiddenPage /> },
    ],
  },
  {
    // Platform admin - its own shell (separate sidebar + branding, light-only
    // by design). Mirrors the legacy platform_admin.html layout. Gated at the
    // shell by requirePlatformAccess; each child route adds a permission check.
    path: '/platform-admin',
    element: <PlatformAdminShell />,
    errorElement: <RouteErrorPage />,
    loader: requirePlatformAccess(),
    children: [
      // Index needs the same permission as the audit feed it reads, so a
      // platform-tenant member without admin.audit.view lands on /403
      // instead of an empty Overview that 403s its own data fetch.
      { index: true, element: <PlatformOverviewPage />, loader: requirePermission('admin.audit.view') },
      {
        path: 'tenants',
        element: <PlatformTenantsPage />,
        loader: requirePermission('admin.tenants.view'),
      },
      {
        path: 'users',
        element: <PlatformCustomerUsersPage />,
        loader: requirePermission('admin.users.view'),
      },
      {
        path: 'staff',
        element: <PlatformStaffPage />,
        loader: requirePermission('admin.staff.manage'),
      },
      {
        path: 'invitations',
        element: <PlatformStaffInvitationsPage />,
        loader: requirePermission('admin.staff.invite'),
      },
      {
        path: 'roles',
        element: <PlatformStaffRolesPage />,
        loader: requirePermission('admin.staff.roles'),
      },
      {
        path: 'audit',
        element: <PlatformAuditPage />,
        loader: requirePermission('admin.audit.view'),
      },
      {
        path: 'tools',
        element: <PlatformDbToolsPage />,
        loader: requirePermission('admin.tools'),
      },
      {
        path: 'managed-payouts',
        element: <PlatformManagedPayoutsPage />,
        loader: requirePermission('admin.managed_payouts.view'),
      },
      {
        path: 'contact-messages',
        element: <PlatformContactMessagesPage />,
        loader: requirePermission('admin.contact_messages.view'),
      },
      {
        path: 'contact-messages/:id',
        element: <PlatformContactMessageDetailPage />,
        loader: requirePermission('admin.contact_messages.view'),
      },
      {
        path: 'announcements',
        element: <PlatformAnnouncementsPage />,
        loader: requirePermission('admin.announcements.view'),
      },
    ],
  },
  {
    // Auth routes - pathless layout route, children own their full paths.
    // This sits at top-level (sibling to AppShell) so visiting /login doesn't
    // trip AppShell's requireAuth loader.
    element: <AuthLayout />,
    children: [
      { path: '/login', element: <LoginPage />, handle: { authWide: true } },
      { path: '/signup', element: <SignupPage />, handle: { authWide: true } },
      { path: '/forgot-password', element: <ForgotPasswordPage /> },
      { path: '/reset-password', element: <ResetPasswordPage /> },
      { path: '/verify-email', element: <VerifyEmailPage /> },
      { path: '/accept-invite', element: <AcceptInvitePage /> },
      { path: '/session-expired', element: <SessionExpiredPage /> },
      { path: '/logout', element: <LogoutPage /> },
      { path: '/admin-login', element: <AdminLoginPage /> },
      // Workspace picker - renders both for post-login multi-tenant flow
      // (pending_session token in sessionStorage) and for a signed-in user
      // who clicked "Switch workspace" from the profile menu.
      { path: '/workspaces', element: <WorkspacePickerPage /> },
    ],
  },
  // Plaid OAuth callback - top-level, no auth guard. Banks redirect here
  // with ?oauth_state_id=... after the user approves the link; the page
  // resumes Plaid.create() with receivedRedirectUri and hands back to /plaid.
  { path: '/plaid/oauth-callback', element: <PlaidOAuthCallbackPage /> },
  // Public hosted invoice payment page. No auth - the pay-token in the URL
  // is the authorization. Sits at top level so AppShell's requireAuth
  // doesn't gate it.
  { path: '/pay/:token', element: <HostedInvoicePaymentPage /> },
  // Public, unauthenticated customer portal. One token lists every open
  // invoice for that customer. Sits top-level so AppShell's requireAuth
  // doesn't gate it.
  { path: '/portal/:token', element: <CustomerPortalPage /> },
  // Public, unauthenticated Smart Chat share. The /chat/share/:token
  // route renders a standalone transcript so recipients outside the
  // tenant can view the chat without a login. Sits at the top level
  // (sibling to AppShell) so it doesn't trip the requireAuth loader.
  { path: '/chat/share/:token', element: <SharedChatPage /> },
  { path: '*', element: <NotFoundPage /> },
]

// Post-Phase-7: React app now lives at the site root. Legacy /app/* URLs
// continue to work because the Express SPA fallback serves index.html for
// any path, and React Router treats /app/dashboard and /dashboard as the
// same route when basename is '/'.
export const router = createBrowserRouter(routes, { basename: '/' })
